import React, { useEffect } from 'react'
import { ChevronRightIcon, HamburgerIcon } from '@chakra-ui/icons'
import { Badge, Box, Button, Center, Flex, HStack, Stack, Text, Tooltip, useMediaQuery } from '@chakra-ui/react'
import { AUTH_MOBILE_ACCENT, DARK_GREY, DARK_PAGE_BG, DARK_TEXT_MUTED, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import StateBadge from 'components/StateBadge'
import { EStatus } from 'constants/workplan'
import { PracticeEventStatus } from 'configs/constants/enums/schedule'
import SvgIcon from 'components/SvgIcon'
import logo from 'assets/images/howfactory.png'
import logoSm from 'assets/images/logo.png'
import classNames from 'classnames'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import startCase from 'lodash/startCase'
import { observer } from 'mobx-react'
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { toggleFavorite } from 'API/favorite'
import HeaderBar from 'components/HeaderBar'
import PlayPageHeader from 'components/HeaderBar/PlayPageHeader'
import { isBasicUserByMembership } from 'utils/auth/role'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { HEADER_TITLE } from 'constants/index'
import CompanyPageHeader from 'pages/CompanyDetailPage/components/CompanyPageHeader'
import OrganizationDetailPageHeader from 'pages/OrganizationDetailPage/OrganizationDetailPageHeader'
import OrganizationReportHeader from 'pages/OrganizationReportPage/OrganizationReportHeader'
import routes from 'routes'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { EFeatureFlags } from 'constants/featureFlags'
import MobileSubPageHeader from 'components/MobileSubPageHeader'
import { MOBILE_MEDIA_QUERY } from 'constants/mobile-page-template'
import { useActivePlayProfile } from 'hooks/use-active-play-profile'
import UserPageHeader from '../../pages/GroupUserPage/components/UserPageHeader'
import NotificationBell from './components/NotificationBell'
import FCMStatus from 'components/FCMStatus'
import ProfileDropdown from './components/ProfileDropdown'
import SearchDropdown from './components/SearchDropdown'
import TopbarSearch from './components/TopbarSearch'
import { profileMenus } from './constants'
import styles from './styles.module.scss'
import MessageButton from './components/MessageButton'
import FirebaseDebugger from 'components/FirebaseDebugger'
import OrgSwitcher from 'components/OrgSwitcher'

type TopbarProps = {
  hideLogo?: boolean
  showSidebarMobile: boolean
  navCssClasses?: string
  toggleSidebarCollapse: () => void
  setShowSidebarMobile: () => void
  /**
   * When true, suppress the legacy nav chrome (search/profile/bell row) and
   * render only the route-aware HeaderBars below it. Used when this component
   * is mounted under PlayTopbar — the dark chrome is already provided by
   * PlayTopbar, this component just contributes the per-page breadcrumb /
   * title bar that lives inside the topbar slot.
   */
  hideChrome?: boolean
}

const Topbar = (props: TopbarProps) => {
  const hideLogo = props.hideLogo || false
  const hideChrome = props.hideChrome || false
  const navCssClasses = props.navCssClasses || ''
  const containerCssClasses = !hideLogo ? 'container-fluid' : ''
  const location = useLocation()
  const [isMobile] = useMediaQuery(MOBILE_MEDIA_QUERY)
  // Schedule pages render their own dark header at ≤1024px (SchedulePage /
  // ScheduleAssignPage use that breakpoint), so suppress this route breadcrumb
  // on the same width to avoid a duplicate empty header bar (the extra divider).
  const [isScheduleMobile] = useMediaQuery('(max-width: 1024px)')
  // Same check SchedulePage makes: only an athlete is handed the player-mobile
  // schedule, so only an athlete has a header of their own down there.
  const { active: activePlayProfile } = useActivePlayProfile()
  const isAthleteViewer = activePlayProfile?.role === 'player' || activePlayProfile?.role === 'child'
  // The schedule event detail hands an athlete the shared sub-page back bar from
  // inside the page below 1024px, so the app header steps aside there — for that
  // viewer only. A coach is handed the ordinary event view and still needs the
  // header: suppressing it for everyone took the coach's breadcrumb and Manage
  // with it, and on a tablet nothing replaced them. The schedule *list* keeps its
  // header for every viewer; the player view no longer titles itself.
  const hideScheduleEventHeader = hideChrome && isScheduleMobile && isAthleteViewer
  const {
    processStore,
    searchStore,
    favoriteStore,
    userStore,
    collectionStore,
    groupStore,
    reportStore,
    planStore,
    scheduleStore,
    trainingPlanDetailStore,
    trainingAssignmentStore,
    metricStore,
    organizationStore,
    authStore
  } = useStores()
  const navigate = useNavigate()
  const { process } = processStore
  const { userDetail } = userStore
  const { searchText, totalResults } = searchStore
  const { collection } = collectionStore
  const { reportDetail, companyReportDetail } = reportStore
  const pathname = location?.pathname
  const isMatchedProcessDetail = !!matchPath('/drills/:processId', pathname)
  const isMatchedGroupMemberDetail = !!matchPath('/groups/:groupId', pathname)
  const isMatchedProcessList = !!matchPath(routes.processes.value, pathname) && !isMatchedProcessDetail
  const isMatchedArchivePage = !!matchPath(routes.archive.value, pathname)
  const isMatchedUserDetailPage = !!matchPath('/users/:userId', pathname)
  // The player membership view renders its own PlayPageHeader (org > player, Done).
  const isPlayerMemberDetail = isMatchedUserDetailPage && new URLSearchParams(location.search).has('membershipId')
  const isMatchedCommonLibraryPage = !!matchPath(routes.commonLibrary.value, pathname)
  const isMatchedSearchPage = !!matchPath(routes.search.value, pathname)
  // Manage mode on the Stacks list is a coach capability. Measured on the
  // cross-team max role, like the rest of the app's chrome: the active-org
  // membership is null in the cross-team context and falls back to PLAYER.
  const canManageStacks = authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)
  const isMatchedGroupPage = !!matchPath(routes.groups.value, pathname) && !isMatchedGroupMemberDetail
  const isMatchedCompanyDetail = !!matchPath(`${routes.admin.report.value}/:organizationId`, pathname)
  const isMatchedOrganizationReportDetail = !!matchPath(`${routes.report.organization.value}/:id`, pathname)
  const isMatchedOrganizationReport =
    !!matchPath(routes.report.organization.value, pathname) && !isMatchedOrganizationReportDetail
  const isMatchedCollectionDetail = !!matchPath(`${routes.collections.value}/:collectionId`, pathname)
  const isMatchedCollectionPage = !!matchPath(routes.collections.value, pathname) && !isMatchedCollectionDetail
  const isMatchedWorkPlanDetail = !!matchPath(`${routes.plans.value}/:id`, pathname)
  const isMatchedTrainingPlanDetail = !!matchPath(`${routes.plans.value}/training/:id`, pathname)
  const isMatchedUserWorkplanDetail = !!matchPath(`/station/:id`, pathname)
  const isMatchedPlansPage =
    !!matchPath(routes.plans.value, pathname) && !isMatchedWorkPlanDetail && !isMatchedTrainingPlanDetail
  const isMatchedSchedulePage = !!matchPath(routes.schedule.value, pathname)
  const isMatchedScheduleAssignPage = !!matchPath('/schedule/:eventId', pathname)
  const isMatchedMetricPage = !!matchPath(routes.setting.metric.value, pathname)
  const isMatchedTrainingAssignmentDetail = !!matchPath('/training-assignments/:id', pathname)
  const isMatchedTrainingAssignmentPage =
    !!matchPath(routes.plans.trainingAssignments.value, pathname) && !isMatchedTrainingAssignmentDetail
  // Create-team form — its breadcrumb renders here (sticky, like the other pages)
  // instead of inside the page body.
  //
  // /teams/:teamId is deliberately NOT handled here any more: TeamDetailPage renders
  // its own PlayPageHeader, so keeping this bar would stack two breadcrumbs. Its
  // Manage/Done toggle went with it — the Settings tab owns Save and Cancel now.
  const isMatchedTeamFormNew = !!matchPath(routes.teams.new.value, pathname)
  // The player's assignment list is named nowhere else — `/dashboard`,
  // `/notifications`, `/messages` and `/player-profile` all title themselves
  // inside the page, so a breadcrumb row there would say the name twice.
  const isMatchedPlayerAssignments = !!matchPath(routes.playerAssignments.value, pathname)

  const teamFormTitle = 'Create new organization'
  // Feature flag: hide Practice Plans header bars when disabled
  const { value: isWorkPlansEnabled } = useFeatureFlagValue(EFeatureFlags.WORK_PLANS, true)
  const isProcessArchived = process?.archivedAt && process?.archivedAt !== null
  const [isProcessFavorite, setIsProcessFavorite] = React.useState<boolean>(false)
  /**
   * Opens the left menu - mobile
   */
  function openLeftMenu() {
    props.setShowSidebarMobile()
    if (document.body) document.body.classList.toggle('sidebar-enable')
  }

  function gotoInboxPage(): void {
    navigate(routes.messages.value)
  }

  function gotoSearchPage(): void {
    searchStore.clearSearchResults()
    searchStore.setManageMode(false)
    searchStore.clearSearchFilter()
    searchStore.setDisplayTypes([EEsGlobalSearchCollection.PROCESS])
    searchStore.setSearchText('')
    searchStore.changeSearchFilter({
      collections: [],
      groups: [],
      sort: ''
    })
    searchStore.search()
    navigate(`${routes.search.value}?searchText=&page=1`)
  }

  function toggleFavoriteProcess(): void {
    setIsProcessFavorite(!isProcessFavorite)
    toggleFavorite({ processId: process?.id })
  }

  useEffect(() => {
    process?.id &&
      favoriteStore.getIsFavorite({ processId: process?.id }).then(res => {
        setIsProcessFavorite(res)
      })
  }, [process])

  useEffect(() => {
    const parts = location.pathname
      .split('/')
      .filter(part => !!part)
      .map(part => startCase(part))

    const lastPartIndex = parts.length - 1
    switch (true) {
      case isMatchedProcessDetail:
        parts.splice(lastPartIndex, 0, process?.name ?? '')
        break
      case isMatchedUserDetailPage:
        parts.splice(lastPartIndex, 0, userDetail?.fullName ?? '')
        break
      case isMatchedCollectionDetail:
        parts.splice(lastPartIndex, 0, collection?.name ?? '')
        break
      case isMatchedOrganizationReportDetail:
        parts.splice(lastPartIndex, 0, reportDetail?.name ?? '')
        break
      case isMatchedCompanyDetail:
        parts.splice(lastPartIndex, 0, companyReportDetail?.name ?? '')
        break
      case isMatchedWorkPlanDetail:
        parts.splice(lastPartIndex, 0, planStore?.selectedWorkplan?.name ?? '')
        break
      case isMatchedTrainingPlanDetail:
        parts.splice(lastPartIndex, 0, trainingPlanDetailStore?.trainingPlanDetail?.name ?? '')
        break
      case isMatchedUserWorkplanDetail:
        parts.splice(0, parts.length, planStore?.stationDetail?.name ?? '')
        break
      case isMatchedScheduleAssignPage:
        parts.splice(lastPartIndex, 0, scheduleStore?.currentEvent?.name ?? '')
        break
      case isMatchedTrainingAssignmentDetail:
        parts.splice(lastPartIndex, 0, trainingAssignmentStore?.assignmentDetail?.name ?? '')
        break
    }

    const formattedTitle = parts.join(' - ')
    document.title = `${HEADER_TITLE}` + (formattedTitle ? ` - ${formattedTitle}` : '')
  }, [
    location.pathname,
    process?.name,
    userDetail?.fullName,
    collection?.name,
    reportDetail?.name,
    companyReportDetail?.name,
    planStore?.selectedWorkplan?.name,
    planStore?.workplanDetail?.name,
    trainingPlanDetailStore?.trainingPlanDetail?.name,
    planStore?.stationDetail?.lineBalanceName,
    planStore?.stationDetail?.name,
    scheduleStore?.currentEvent?.name,
    trainingAssignmentStore?.assignmentDetail?.name
  ])

  return (
    <React.Fragment>
      <div className={cx(styles.container, hideChrome && styles.stickyHeader)}>
        {/* When chrome is hidden (mounted under PlayTopbar), drop the
            `navbar-custom` class so we don't grab its `position: fixed; top: 0`
            slot — that would overlay PlayTopbar with a white rectangle. */}
        <div className={cx(!hideChrome && `navbar-custom ${navCssClasses}`, styles.navSpacing)}>
          {!hideChrome && (
            <div className={cx(styles.primaryNavSpacing, containerCssClasses)}>
              {!hideLogo && (
                <Link to="/" className="topnav-logo">
                  <span className="topnav-logo-lg">
                    <img src={logo} alt="logo" height="16" />
                  </span>
                  <span className="topnav-logo-sm">
                    <img src={logoSm} alt="logo" height="16" />
                  </span>
                </Link>
              )}

              <ul className="list-unstyled topbar-right-menu float-right mb-0">
                <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                  <SvgIcon
                    iconName="process-button"
                    size={40}
                    style={{ margin: 'auto 8px', cursor: 'pointer' }}
                    onClick={gotoSearchPage}
                  />
                </li>
                <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                  {/* <SvgIcon iconName="inbox-mobile-button" size={24} style={{ cursor: 'pointer' }} onClick={gotoInboxPage} /> */}
                  <MessageButton />
                </li>
                <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                  <NotificationBell />
                </li>
                <li className="notification-list d-md-none">
                  <ProfileDropdown menuItems={profileMenus} />
                </li>
              </ul>

              <button
                className="button-menu-mobile open-left disable-btn"
                onClick={openLeftMenu}
                aria-label="Toggle sidebar"
                title="Toggle sidebar"
              >
                <HamburgerIcon className={classNames(styles.collapseIcon, styles.mobileCollapseIcon)} />
              </button>

              <ul className="list-unstyled topbar-right-menu mb-0">
                <li className="notification-list topbar-dropdown d-md-none" style={{ display: 'inline-block' }}>
                  <SearchDropdown />
                </li>
                {/* <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                <SvgIcon iconName="process-button" size={24} style={{ margin: 'auto 8px', cursor: 'pointer' }} onClick={gotoSearchPage} />
              </li> */}
              </ul>
              {props.showSidebarMobile ? <div className={classNames(styles.backgroundButtonMenuMobile)} /> : ''}

              <div className={styles.leftSideWrapper}>
                <HamburgerIcon className={styles.collapseIcon} onClick={props.toggleSidebarCollapse} />
                <Center width="full" gap={2}>
                  <TopbarSearch />
                  <Center height="70px">
                    <Tooltip label="My Stacks">
                      <Button
                        boxSize={10}
                        background="white"
                        border="1px solid #E2E8F0"
                        padding={0}
                        onClick={gotoSearchPage}
                      >
                        <SvgIcon iconName="process-icon" size={20} style={{ margin: 'auto 0' }} />
                      </Button>
                    </Tooltip>
                  </Center>
                </Center>
                <HStack width="290px">
                  {/* <SvgIcon iconName="inbox-button" size={24} style={{ cursor: 'pointer' }} onClick={gotoInboxPage} /> */}
                  <MessageButton />
                  <Flex>
                    <NotificationBell />
                  </Flex>
                  <OrgSwitcher />
                  <ProfileDropdown menuItems={profileMenus} />
                </HStack>
              </div>
            </div>
          )}
          {/* Stack Detail draws its own header on a phone (back, name, state,
              actions) because a breadcrumb plus a Manage pill does not survive
              that width — same query as the page's own, so the two can never
              both be on screen. */}
          {isMatchedProcessDetail && !isMobile && (
            <HeaderBar
              isDark
              title={
                <HStack spacing={2} minW={0}>
                  <Text
                    color={AUTH_MOBILE_ACCENT}
                    cursor="pointer"
                    m={0}
                    flexShrink={0}
                    onClick={() =>
                      process?.archivedAt
                        ? navigate(routes.archive.value, { replace: true })
                        : process?.isPublished
                          ? gotoSearchPage()
                          : navigate(routes.processes.value, { replace: true })
                    }
                  >
                    {process?.archivedAt ? 'Archives' : process?.isPublished ? 'Stacks' : 'Drafts'}
                  </Text>
                  <ChevronRightIcon color={DARK_TEXT_MUTED} flexShrink={0} />
                  <Text color={DARK_TEXT_PRIMARY} fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                    {process?.name ?? ''}
                  </Text>
                  {process?.isPublished === false && (
                    // Warning/500 straight from the design. The theme has no
                    // `warning` scale — `yellow.500` is a different amber — so the
                    // token cannot be referenced yet.
                    <Badge
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap="8px"
                      flexShrink={0}
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight={700}
                      letterSpacing="0.06em"
                      borderRadius="4px"
                      padding="2px 6px"
                      background="rgba(242, 177, 52, 0.13)"
                      color="#F2B134"
                    >
                      Draft
                    </Badge>
                  )}
                  {isProcessArchived && (
                    <Badge
                      flexShrink={0}
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight={700}
                      letterSpacing="0.06em"
                      borderRadius="4px"
                      px={2}
                      background="rgba(194, 199, 214, 0.12)"
                      color={DARK_TEXT_MUTED}
                    >
                      Archived
                    </Badge>
                  )}
                  <SvgIcon
                    iconName={isProcessFavorite ? 'favorite' : 'un_favorite'}
                    size={20}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={toggleFavoriteProcess}
                  />
                </HStack>
              }
              controlBy="processDetail"
            />
          )}
          {isMatchedUserDetailPage && !isPlayerMemberDetail && (
            <HeaderBar
              isDark
              title={
                <HStack spacing={1}>
                  <Text
                    color={DARK_TEXT_MUTED}
                    cursor="pointer"
                    _hover={{ color: DARK_TEXT_PRIMARY }}
                    onClick={() => navigate(routes.organization.value, { replace: true })}
                  >
                    Users
                  </Text>
                  <ChevronRightIcon color={DARK_TEXT_MUTED} />
                  <Text color={DARK_TEXT_PRIMARY}>{userDetail?.fullName ?? ''}</Text>
                </HStack>
              }
              controlBy="userDetailPage"
              subPageTitle={userDetail?.fullName ?? ''}
              onBack={() => navigate(routes.organization.value, { replace: true })}
            />
          )}

          {isMatchedProcessList && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Drafts
                </Text>
              }
              showManage={processStore?.canUserEditInProcessList}
              isManageActive={processStore?.isManageModeInProcessList}
              onToggleManage={() => processStore?.setManageModeInList(!processStore?.isManageModeInProcessList)}
            />
          )}

          {isMatchedSearchPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  {searchText
                    ? `${totalResults} results for "${searchText}"`
                    : searchStore.countFilter() > 0
                      ? `${totalResults} results`
                      : // "Stacks", never "My Stacks": the list is every published Stack of
                        // the Organization for every role — it was never scoped to the viewer.
                        'Stacks'}
                </Text>
              }
              showManage={canManageStacks}
              isManageActive={searchStore?.isManageMode}
              onToggleManage={() => searchStore?.setManageMode(!searchStore?.isManageMode)}
            />
          )}

          {isMatchedCollectionPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Collections
                </Text>
              }
              showManage={
                !isBasicUserByMembership(authStore.currentMembership) ||
                userStore?.currentUserGroupMembers?.some(m => m?.permissionId === IGroupMemberPermissionId.EDITOR)
              }
              isManageActive={collectionStore?.isManageMode}
              onToggleManage={() => collectionStore?.setManageMode(!collectionStore?.isManageMode)}
            />
          )}

          {isMatchedCollectionDetail && (
            <HeaderBar
              // Same dark breadcrumb as the Stack detail header above.
              isDark
              title={
                <HStack spacing={2} minW={0}>
                  <Text
                    color={AUTH_MOBILE_ACCENT}
                    cursor="pointer"
                    m={0}
                    flexShrink={0}
                    onClick={() =>
                      navigate(collection?.archivedAt ? routes.archive.value : routes.collections.value, {
                        replace: true
                      })
                    }
                  >
                    {collection?.archivedAt ? 'Archives' : 'Collections'}
                  </Text>
                  <ChevronRightIcon color={DARK_TEXT_MUTED} flexShrink={0} />
                  <Text color={DARK_TEXT_PRIMARY} fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                    {collection?.name ?? ''}
                  </Text>
                  {collection?.isPublished === false && (
                    <Badge
                      flexShrink={0}
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight={700}
                      letterSpacing="0.06em"
                      borderRadius="4px"
                      padding="2px 6px"
                      background="rgba(242, 177, 52, 0.13)"
                      color="#F2B134"
                    >
                      Draft
                    </Badge>
                  )}
                </HStack>
              }
              controlBy={'collectionDetailPage'}
              subPageTitle={collection?.name ?? ''}
              onBack={() =>
                navigate(collection?.archivedAt ? routes.archive.value : routes.collections.value, { replace: true })
              }
              subPageTags={collection?.isPublished === false ? [{ label: 'Draft', tone: 'draft' }] : undefined}
            />
          )}

          {isMatchedGroupPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Player Groups
                </Text>
              }
              showManage={!isBasicUserByMembership(authStore.currentMembership)}
              isManageActive={groupStore?.isManageModeInGroupList}
              onToggleManage={() => groupStore?.setManageModeInGroupList(!groupStore?.isManageModeInGroupList)}
            />
          )}

          {/* Team Members (/users) renders its own dark header inside the page. */}

          {isMatchedArchivePage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Archives
                </Text>
              }
              showManage={!isBasicUserByMembership(authStore.currentMembership)}
              isManageActive={processStore?.isManageModeInArchive}
              onToggleManage={() => processStore?.setManageModeInArchive(!processStore?.isManageModeInArchive)}
            />
          )}

          {isMatchedCommonLibraryPage && <HeaderBar title="Common Library" controlBy="commonLibraryList" />}

          {isMatchedMetricPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Metric
                </Text>
              }
              showManage={!isBasicUserByMembership(authStore.currentMembership)}
              isManageActive={metricStore?.isManageMode}
              onToggleManage={() => metricStore?.setManageMode(!metricStore?.isManageMode)}
            />
          )}

          {isWorkPlansEnabled && isMatchedTrainingAssignmentPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Assignments
                </Text>
              }
              // Cross-team capability check: the active-org membership can be null
              // (falls back to PLAYER), which would wrongly hide Manage from a
              // coach. Gate on the highest role across all teams instead.
              showManage={authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)}
              isManageActive={trainingAssignmentStore?.isManageMode}
              onToggleManage={() => trainingAssignmentStore?.setManageMode(!trainingAssignmentStore?.isManageMode)}
            />
          )}

          {isWorkPlansEnabled && isMatchedTrainingAssignmentDetail && (
            <HeaderBar
              // The assignment detail renders on a dark surface, like the plan detail.
              isDark
              title={
                <HStack color="primary.500" spacing={1}>
                  <Text
                    cursor="pointer"
                    onClick={() => navigate(routes.plans.trainingAssignments.value, { replace: true })}
                  >
                    Assignments
                  </Text>
                  <ChevronRightIcon color="rgba(255,255,255,0.64)" />
                  <HStack spacing={2}>
                    <Text color="#FFFFFF">{trainingAssignmentStore?.assignmentDetail?.name ?? ''}</Text>
                    {trainingAssignmentStore?.assignmentDetail?.status === 1 && (
                      <Badge
                        size="md"
                        variant="outline"
                        borderRadius="6px"
                        px={2}
                        py={1}
                        fontSize="14px"
                        fontWeight={600}
                        borderWidth="1px"
                        backgroundColor="rgba(255,179,102,0.12)"
                        borderColor="rgba(255,179,102,0.35)"
                        color="#FFB366"
                        shadow="none"
                        textTransform="none"
                      >
                        Draft
                      </Badge>
                    )}
                  </HStack>
                </HStack>
              }
              controlBy="trainingAssignmentDetailPage"
              subPageTitle={trainingAssignmentStore?.assignmentDetail?.name ?? ''}
              onBack={() => navigate(routes.plans.trainingAssignments.value, { replace: true })}
              subPageTags={
                trainingAssignmentStore?.assignmentDetail?.status === 1
                  ? [{ label: 'Draft', tone: 'draft' }]
                  : undefined
              }
            />
          )}

          {isWorkPlansEnabled && isMatchedPlansPage && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  Plans
                </Text>
              }
              showManage={!isBasicUserByMembership(authStore.currentMembership)}
              isManageActive={planStore?.isManageModeInPlansList}
              onToggleManage={() => planStore?.setManageModeInPlansList(!planStore?.isManageModeInPlansList)}
            />
          )}
          {/* On mobile play-chrome the player-mobile schedule provides its own
              dark "Schedule" header, so suppress this breadcrumb bar (which would
              render on a white strip). Coach-desktop keeps the light breadcrumb;
              desktop play-chrome keeps a dark one for cross-team consistency. */}
          {isMatchedSchedulePage && <HeaderBar title="Schedule" controlBy="schedulePage" isDark={hideChrome} />}
          {isWorkPlansEnabled && isMatchedScheduleAssignPage && !hideScheduleEventHeader && (
            <HeaderBar
              title={
                <HStack color="primary.500" spacing={1}>
                  <Text cursor="pointer" onClick={() => navigate(routes.schedule.value, { replace: true })}>
                    Schedule
                  </Text>
                  <ChevronRightIcon color="rgba(255,255,255,0.64)" />
                  <HStack spacing={2}>
                    <Text marginRight="6px !important" color="#FFFFFF">
                      {scheduleStore?.currentEvent?.name ?? 'Event'}
                    </Text>
                    {scheduleStore?.currentEvent?.status === PracticeEventStatus.DRAFT && (
                      <Badge
                        size="md"
                        variant="outline"
                        borderRadius="6px"
                        px={2}
                        py={1}
                        fontSize="14px"
                        fontWeight={600}
                        borderWidth="1px"
                        backgroundColor="#FFF7E6"
                        borderColor="#FFB366"
                        color="#E67E22"
                        shadow="none"
                      >
                        Private
                      </Badge>
                    )}
                  </HStack>
                </HStack>
              }
              // The schedule event detail renders on the same dark surface as the
              // practice-plan detail, so its breadcrumb bar must match rather than
              // sit on a white strip above it.
              isDark
              controlBy="scheduleAssignPage"
              subPageTitle={scheduleStore?.currentEvent?.name ?? 'Event'}
              onBack={() => navigate(routes.schedule.value, { replace: true })}
              subPageTags={
                scheduleStore?.currentEvent?.status === PracticeEventStatus.DRAFT
                  ? [{ label: 'Private', tone: 'draft' }]
                  : undefined
              }
            />
          )}
          {isWorkPlansEnabled &&
            (isMatchedWorkPlanDetail || isMatchedTrainingPlanDetail) &&
            (() => {
              const isTrainingPlan = isMatchedTrainingPlanDetail
              const planName = isTrainingPlan
                ? trainingPlanDetailStore?.trainingPlanDetail?.name
                : planStore?.workplanDetail?.name
              const isTrainingSnapshot = isTrainingPlan
                ? Boolean(trainingPlanDetailStore?.trainingPlanDetail?.isSnapshot)
                : false

              const shouldShowInactiveBadge = isTrainingPlan
                ? typeof trainingPlanDetailStore?.trainingPlanDetail?.status !== 'undefined' &&
                  Number(trainingPlanDetailStore.trainingPlanDetail.status) === EStatus.INACTIVE
                : Number(planStore?.workplanDetail?.status) === EStatus.INACTIVE &&
                  !planStore?.workplanDetail?.originalLineBalanceId

              const inactiveStatus: number = EStatus.INACTIVE

              return (
                <HeaderBar
                  // Both work-plan and training-plan detail pages render on a dark
                  // surface now, so the breadcrumb bar is dark for both.
                  isDark
                  title={
                    <HStack color="primary.500" spacing={1}>
                      <Text cursor="pointer" onClick={() => navigate(routes.plans.value, { replace: true })}>
                        Plans
                      </Text>
                      <ChevronRightIcon color="rgba(255,255,255,0.64)" />
                      <HStack spacing={2}>
                        <Text marginRight="6px !important" color="#FFFFFF">
                          {planName ?? ''}
                        </Text>
                        {shouldShowInactiveBadge && <StateBadge status={inactiveStatus} />}
                      </HStack>
                    </HStack>
                  }
                  subPageTitle={planName ?? ''}
                  onBack={() => navigate(routes.plans.value, { replace: true })}
                  subPageTags={shouldShowInactiveBadge ? [{ label: 'Inactive', tone: 'neutral' }] : undefined}
                  {...(isTrainingPlan
                    ? isTrainingSnapshot
                      ? {}
                      : {
                          isManageMode: trainingPlanDetailStore?.isManageMode,
                          setIsManageMode: (isManageMode: boolean) =>
                            trainingPlanDetailStore?.setManageMode(isManageMode)
                        }
                    : { controlBy: 'workPlanDetail' })}
                />
              )
            })()}

          {isWorkPlansEnabled && isMatchedUserWorkplanDetail && (
            <HeaderBar
              title={<Text color="gray.700">{planStore?.stationDetail?.name ?? 'Station'}</Text>}
              controlBy="workPlanDetail"
              subPageTitle={planStore?.stationDetail?.name ?? 'Station'}
              onBack={() => navigate(routes.plans.value, { replace: true })}
            />
          )}

          {isMatchedGroupMemberDetail && (
            <HeaderBar
              isDark
              title={<UserPageHeader />}
              controlBy="groupMemberList"
              subPageTitle={groupStore?.groupDetail?.name ?? ''}
              onBack={() => navigate(routes.groups.value, { replace: true })}
            />
          )}

          {isMatchedCompanyDetail && (
            <HeaderBar
              title={<CompanyPageHeader />}
              subPageTitle={companyReportDetail?.name ?? ''}
              onBack={() => navigate(routes.admin.report.value)}
            />
          )}

          {isMatchedOrganizationReport && <HeaderBar title={<OrganizationReportHeader />} />}

          {isMatchedOrganizationReportDetail && (
            <HeaderBar
              title={<OrganizationDetailPageHeader />}
              subPageTitle={organizationStore?.organization?.name ?? ''}
              onBack={() => navigate(routes.report.organization.value)}
            />
          )}

          {isMatchedPlayerAssignments && (
            <PlayPageHeader
              breadcrumb={
                <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                  My Assignments
                </Text>
              }
            />
          )}
          {isMatchedTeamFormNew &&
            // On a phone the breadcrumb and the Done pill do not both fit, and the
            // back arrow already is Done: both leave the form for the Teams list.
            (isMobile ? (
              <MobileSubPageHeader title={teamFormTitle} onBack={() => navigate(routes.teams.value)} />
            ) : (
              <Flex align="center" justify="space-between" px="16px" py="16px" gap="12px" bg={DARK_PAGE_BG}>
                <HStack spacing="8px" overflow="hidden" flex={1}>
                  <Box
                    as="span"
                    cursor="pointer"
                    onClick={() => navigate(routes.dashboard.value)}
                    display="inline-flex"
                    flexShrink={0}
                  >
                    <SvgIcon iconName="home" size={20} color={AUTH_MOBILE_ACCENT} />
                  </Box>
                  <ChevronRightIcon color={DARK_TEXT_MUTED} flexShrink={0} />
                  <Text
                    color={DARK_TEXT_MUTED}
                    fontWeight={500}
                    fontSize="16px"
                    m={0}
                    cursor="pointer"
                    onClick={() => navigate(routes.teams.value)}
                    flexShrink={0}
                    _hover={{ color: '#F2F7FA' }}
                  >
                    Teams
                  </Text>
                  <ChevronRightIcon color={DARK_TEXT_MUTED} flexShrink={0} />
                  <Text color="#F2F7FA" fontWeight={600} fontSize="16px" m={0} noOfLines={1}>
                    {teamFormTitle}
                  </Text>
                </HStack>
                <Button
                  onClick={() => navigate(routes.teams.value)}
                  h="32px"
                  px="16px"
                  flexShrink={0}
                  borderRadius="full"
                  bg="transparent"
                  color="#F2F7FA"
                  border={`1px solid ${DARK_GREY}`}
                  fontSize="14px"
                  fontWeight={500}
                  _hover={{ bg: 'rgba(255, 255, 255, 0.06)' }}
                  _active={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  _focusVisible={{ boxShadow: 'none' }}
                >
                  Done
                </Button>
              </Flex>
            ))}
        </div>
      </div>
    </React.Fragment>
  )
}

export default observer(Topbar)
